import React from "react";
import { FiDownload, FiExternalLink, FiFileText, FiHelpCircle } from "react-icons/fi";
import ScreenHeader from "./ScreenHeader";
import { filterEmployeePayrollHistory } from "../utils/payrollHistory";

const RECENT_PAYROLL_SLIP_LIMIT = 12;
const normalizeDocumentSearchText = (document) =>
  [
    document?.documentName,
    document?.label,
    document?.title,
    document?.name,
    document?.fileName,
  ]
    .filter(Boolean)
    .join(" ")
    .trim()
    .toLowerCase();

const isTaxDocument = (document) => {
  const text = normalizeDocumentSearchText(document);
  const compactText = text.replace(/[^a-z0-9]/g, "");

  return compactText.includes("form16") || text.includes("tax") || text.includes("tds");
};

const getTaxDocumentPeriod = (document) =>
  document?.period ||
  document?.periodKey ||
  document?.financialYear ||
  document?.fiscalYear ||
  document?.year ||
  document?.updated ||
  "--";

const PayrollScreen = ({
  loading = false,
  payrollHistory: salarySlips = [],
  taxDocuments = [],
  onAction,
  onDownloadPayslip,
  onPayslipSelect,
}) => {
  const [showAllSlips, setShowAllSlips] = React.useState(false);
  const allSlips = filterEmployeePayrollHistory(salarySlips);
  const visibleSlips = showAllSlips ? allSlips : allSlips.slice(0, RECENT_PAYROLL_SLIP_LIMIT);
  const hasMoreSlips = allSlips.length > RECENT_PAYROLL_SLIP_LIMIT;
  const employeeTaxDocuments = (Array.isArray(taxDocuments) ? taxDocuments : []).filter(isTaxDocument);
  const latestSlip = allSlips[0] || null;

  return (
    <section className="new-hrm-screen new-hrm-payroll-screen">
      <ScreenHeader
        eyebrow="Compensation"
        title="Payroll"
        description="Open or download your released salary slips."
        actions={
          <div className="new-hrm-payroll-header-actions">
            <button
              className="new-hrm-primary-button new-hrm-payroll-header-action"
              type="button"
              onClick={() => onDownloadPayslip(latestSlip)}
              disabled={loading || !latestSlip}
              aria-label="Download latest salary slip PDF"
              title="Download latest salary slip PDF"
            >
              <FiDownload aria-hidden="true" />
              <span>Download PDF</span>
            </button>
            <button
              className="new-hrm-secondary-button new-hrm-payroll-header-action"
              type="button"
              onClick={() => onPayslipSelect(latestSlip)}
              disabled={loading || !latestSlip}
              aria-label="View latest salary slip details"
              title="View latest salary slip details"
            >
              <FiFileText aria-hidden="true" />
              <span>View details</span>
            </button>
            <button
              className="new-hrm-secondary-button new-hrm-payroll-header-action"
              type="button"
              onClick={() => onAction("financeRequest")}
              disabled={loading}
              aria-label="Get payroll help"
              title="Get payroll help"
            >
              <FiHelpCircle aria-hidden="true" />
              <span>Payroll help</span>
            </button>
          </div>
        }
      />
      <div className="new-hrm-payroll-layout new-hrm-payroll-layout--history-only">
        <div className="new-hrm-payroll-stack">
          <section className="new-hrm-panel new-hrm-payroll-history-panel">
            <header className="new-hrm-panel__header new-hrm-panel__header--compact">
              <div>
                <h2>Salary Slip History</h2>
                <p>{showAllSlips ? "Salary slips from April 2025 through the current month." : "Your 12 most recent salary slips released through payroll."}</p>
              </div>
              {hasMoreSlips ? (
                <button className="new-hrm-link-button" type="button" onClick={() => setShowAllSlips((currentValue) => !currentValue)} disabled={loading} aria-expanded={showAllSlips}>
                  {showAllSlips ? "Show Recent" : `View All (${allSlips.length})`}
                </button>
              ) : (
                <button className="new-hrm-link-button" type="button" onClick={() => onPayslipSelect(latestSlip)} disabled={loading || !latestSlip}>View Latest</button>
              )}
            </header>
            {visibleSlips.length ? (
              <div className="new-hrm-payroll-table-wrap">
                <table className="new-hrm-payroll-table">
                  <thead>
                    <tr>
                      <th>Period</th>
                      <th>Net Pay</th>
                      <th>Gross</th>
                      <th>Deductions</th>
                      <th>Pay Date</th>
                      <th>Status</th>
                      <th aria-label="Actions" />
                    </tr>
                  </thead>
                  <tbody>
                    {visibleSlips.map((item) => (
                      <tr key={item.id}>
                        <td data-label="Period">
                          <strong>{item.period}</strong>
                          <span>{item.periodKey || "Salary slip"}</span>
                        </td>
                        <td data-label="Net Pay">{item.net}</td>
                        <td data-label="Gross">{item.gross}</td>
                        <td data-label="Deductions">{item.deductions}</td>
                        <td data-label="Pay Date">{item.payDate || "--"}</td>
                        <td data-label="Status"><em className="new-hrm-status new-hrm-status--posted">{item.status}</em></td>
                        <td data-label="Actions">
                          <div className="new-hrm-payroll-row-actions">
                            <button className="new-hrm-payroll-row-action" type="button" onClick={() => onPayslipSelect(item)} disabled={loading}>
                              <FiExternalLink aria-hidden="true" />
                              View
                            </button>
                            <button className="new-hrm-payroll-row-action new-hrm-payroll-row-action--primary" type="button" onClick={() => onDownloadPayslip(item)} disabled={loading}>
                              <FiDownload aria-hidden="true" />
                              Download
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="new-hrm-empty-state">No released salary slips are available yet.</p>
            )}
          </section>

          <section className="new-hrm-panel new-hrm-payroll-history-panel">
            <header className="new-hrm-panel__header new-hrm-panel__header--compact">
              <div>
                <h2>Tax Documents</h2>
                <p>Form 16 files uploaded on your employee external attachments.</p>
              </div>
            </header>
            {employeeTaxDocuments.length ? (
              <div className="new-hrm-payroll-table-wrap">
                <table className="new-hrm-payroll-table new-hrm-payroll-table--tax-documents">
                  <thead>
                    <tr>
                      <th>Document</th>
                      <th>Period</th>
                      <th>Status</th>
                      <th aria-label="Actions" />
                    </tr>
                  </thead>
                  <tbody>
                    {employeeTaxDocuments.map((document, index) => {
                      const downloadUrl = document.downloadUrl || document.fileUrl || "";
                      return (
                        <tr key={document.id || document.externalAttachmentId || `${document.label}-${index}`}>
                          <td data-label="Document">
                            <strong>{document.label || document.documentName || `Tax document ${index + 1}`}</strong>
                            <span>{document.documentName || "External attachment"}</span>
                          </td>
                          <td data-label="Period">{getTaxDocumentPeriod(document)}</td>
                          <td data-label="Status"><em className="new-hrm-status new-hrm-status--posted">{document.status || "Available"}</em></td>
                          <td data-label="Actions">
                            <div className="new-hrm-payroll-row-actions new-hrm-payroll-row-actions--single">
                              {downloadUrl ? (
                                <a className="new-hrm-secondary-button" href={downloadUrl} target="_blank" rel="noreferrer">Download</a>
                              ) : (
                                <button type="button" disabled>Download</button>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="new-hrm-empty-state">No Form 16 or tax document is available yet.</p>
            )}
          </section>
        </div>
      </div>
    </section>
  );
};

export default PayrollScreen;
